import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import ClientAppShell from "../../components/ClientAppShell";
import ProfileInfo from "../components/profile-info";
import ContentLoader from "../../components/ContentLoader";
import { SmeDashboardHero } from "../../components/client/SmeDashboardHero";
import { useAuth } from "../../auth/AuthContext";
import { getSMEPendingProjectList } from "../../services/smeApi";
import {
  dedupePendingProjects,
  getCompanyName,
  getPendingProjectKey,
  getProjectName,
  readCachedSmePendingProjects,
  writeCachedSmePendingProjects,
  type SmePendingProject,
} from "../../utils/smeProjectUtils";
import "../../assets/css/dashboard.css";
import "../../styles/client-dashboard.css";
import "../../styles/sme-dashboard.css";

const formatSubmittedAt = (project: SmePendingProject) => {
  const raw = project.stateDateTime ?? project.StateDateTime;
  if (!raw) return null;
  const date = new Date(raw as string | number);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleString();
};

const SMEDashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [selectedItem, setSelectedItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [pendingProjects, setPendingProjects] = useState<SmePendingProject[]>(
    []
  );

  useEffect(() => {
    let cancelled = false;

    const loadPendingWork = async () => {
      const cached = readCachedSmePendingProjects();
      if (cached?.length) {
        setPendingProjects(dedupePendingProjects(cached));
        setLoading(false);
      } else {
        setLoading(true);
      }
      setLoadError("");

      try {
        const result = await getSMEPendingProjectList();
        if (cancelled) return;

        const projects = dedupePendingProjects(
          result?.data?.projects ?? result?.projects ?? []
        );
        setPendingProjects(projects);
        writeCachedSmePendingProjects(projects);
      } catch (error) {
        if (cancelled) return;
        console.error("Error loading SME dashboard:", error);
        setPendingProjects([]);
        setLoadError("Unable to load pending assessments right now.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadPendingWork();

    return () => {
      cancelled = true;
    };
  }, []);

  const recentProjects = useMemo(
    () => pendingProjects.slice(0, 6),
    [pendingProjects]
  );

  const handleMenuSelection = (item) => {
    setSelectedItem(item);
    return item;
  };

  return (
    <ClientAppShell
      title="SME Dashboard"
      dataNav="sme-dashboard"
      selectedItem={selectedItem}
      onMenuSelect={handleMenuSelection}
      profileSlot={<ProfileInfo />}
    >
      <div className="sme-dash">
        <SmeDashboardHero
          userName={user?.userName}
          pendingCount={pendingProjects.length}
        />

        <div className="sme-dash__panel">
          {loading ? (
            <ContentLoader compact label="Loading your work queue…" />
          ) : (
            <>
              <div className="dash-role-stats" aria-live="polite">
                <div className="dash-role-stat dash-role-stat--accent">
                  <span className="dash-role-stat__label">Under review</span>
                  <span className="dash-role-stat__value">
                    {pendingProjects.length.toLocaleString()}
                  </span>
                  <span className="dash-role-stat__hint">
                    Assessments that may need your attention
                  </span>
                </div>
                <div className="dash-role-stat">
                  <span className="dash-role-stat__label">Workspace</span>
                  <span className="dash-role-stat__value dash-role-stat__value--text">
                    Assessment Tracking
                  </span>
                  <Link
                    to="/sme/assesstracking"
                    className="dash-role-action-link"
                  >
                    Open tracking →
                  </Link>
                </div>
              </div>

              {loadError ? (
                <p className="sme-dash__error" role="alert">{loadError}</p>
              ) : null}

              <h2 className="dash-role-section-title">
                {pendingProjects.length === 0
                  ? "Work queue"
                  : "Recent pending projects"}
              </h2>

              {pendingProjects.length === 0 ? (
                <div className="dash-role-empty">
                  <p className="dash-role-empty__title">No pending assessments</p>
                  <p className="dash-role-empty__hint">
                    When clients submit assessments assigned to you, they will
                    appear here and in Assessment Tracking.
                  </p>
                  <Link
                    to="/sme/assesstracking"
                    className="dash-admin-home__card dash-role-empty__cta"
                  >
                    <span className="dash-admin-home__card-label">
                      Go to Assessment Tracking
                    </span>
                  </Link>
                </div>
              ) : (
                <>
                  <div className="dash-role-list">
                    {recentProjects.map((project) => {
                      const submittedAt = formatSubmittedAt(project);
                      const company = getCompanyName(project);
                      return (
                        <button
                          type="button"
                          key={getPendingProjectKey(project)}
                          className="dash-role-list__item"
                          onClick={() => navigate("/sme/assesstracking")}
                        >
                          <span className="dash-role-list__title">
                            {getProjectName(project) || "Untitled project"}
                          </span>
                          <span className="dash-role-list__meta">
                            {company ? `${company}` : "Organization N/A"}
                            {project.username ? ` · ${project.username}` : ""}
                            {submittedAt ? ` · ${submittedAt}` : ""}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                  {pendingProjects.length > recentProjects.length ? (
                    <p className="dash-role-list__more">
                      Showing {recentProjects.length} of{" "}
                      {pendingProjects.length}.{" "}
                      <Link to="/sme/assesstracking">
                        View all in Assessment Tracking
                      </Link>
                    </p>
                  ) : (
                    <div className="dash-role-actions">
                      <Link
                        to="/sme/assesstracking"
                        className="dash-role-action-link"
                      >
                        Continue in Assessment Tracking →
                      </Link>
                    </div>
                  )}
                </>
              )}
            </>
          )}
        </div>
      </div>
    </ClientAppShell>
  );
};

export default SMEDashboard;
